import React, { useMemo, useState } from "react";
import { Add20Regular, Search20Regular } from "@fluentui/react-icons";
import { labelForUserKey, type AppUser } from "../../services/userService";
import type {
  ChatConversation,
  ChatTeamSummary,
  ChatLastMessagePreview,
} from "../../services/chatService";
import { useChat } from "../../context/ChatContext";
import { usePresence } from "./usePresence";
import { PresenceDot } from "./PresenceDot";
import { InitialsAvatar } from "./chatUi";
import { AUB, INK, INK2, LINE, PLUM, GOLD, CREAM, ACTIVE_BG, fmtListTime, teamKuerzel, avatarColor, gruppenName } from "./chatTheme";

/** Vorschautext für die 2. Zeile: eigene Nachricht → „Du: …", im Team zusätzlich
 *  der Vorname des Absenders, in der DM nur der Text. */
const previewText = (
  lm: ChatLastMessagePreview | null | undefined,
  isTeam: boolean,
): string => {
  if (!lm) return "";
  const body = lm.body || "";
  if (lm.isOwn) return `Du: ${body}`;
  if (isTeam) {
    const first = (lm.authorName || "").trim().split(/\s+/)[0] || "";
    return first ? `${first}: ${body}` : body;
  }
  return body;
};

type Tab = "alle" | "direkt" | "teams";
const TAB_LABEL: Record<Tab, string> = { alle: "Alle", direkt: "Direkt", teams: "Teams" };

type Props = {
  conversations: ChatConversation[];
  teams: ChatTeamSummary[];
  users: AppUser[];
  currentUserId: string | null;
  activeId: string | null;
  onSelect: (id: string) => void;
  onOpenTeam: (teamId: string) => void;
  /** Klick auf eine Person ohne bisherige DM → getOrCreateDm + öffnen. */
  onOpenDmUser: (userId: string) => void;
  /** „+": Fenster für eine Nachricht an mehrere — Empfänger per @ im Text. */
  onNewChat: () => void;
  /** Das Fenster ist gerade offen (hebt den „+"-Knopf hervor). */
  newChatOpen?: boolean;
};

/** Eine Zeile der Direktnachrichten: JEDE Person steht in der Liste, mit oder
 *  ohne bisherige Unterhaltung (PO 04.10.2026). */
type DmZeile = { key: string; userId: string | null; name: string; conv: ChatConversation | null };

const dmOtherId = (c: ChatConversation, me: string | null): string | null => {
  if (c.type !== "dm" || !c.dmKey) return null;
  const parts = c.dmKey.split("|");
  return parts.find((p) => p !== me) ?? parts[0] ?? null;
};

export const ConversationList: React.FC<Props> = ({
  conversations,
  teams,
  users,
  currentUserId,
  activeId,
  onSelect,
  onOpenTeam,
  onOpenDmUser,
  onNewChat,
  newChatOpen = false,
}) => {
  const chat = useChat();
  const [tab, setTab] = useState<Tab>("alle");
  const [query, setQuery] = useState("");
  const nameOf = (key: string) => {
    const lc = key.trim().toLowerCase();
    const u = users.find((x) => x.id.toLowerCase() === lc || (x.email ?? "").toLowerCase() === lc);
    if (u) {
      const fl = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
      return fl || u.name || u.username || u.email || labelForUserKey(key, users);
    }
    return labelForUserKey(key, users);
  };

  // Direktnachrichten: alle Personen, alphabetisch — eine bestehende DM hängt an
  // ihrer Person, statt sie nach oben zu sortieren. `dmKey` trägt historisch auch
  // E-Mail/Kürzel statt der id, daher der Abgleich über alle drei.
  const dmZeilen = useMemo<DmZeile[]>(() => {
    const convByPartner = new Map<string, ChatConversation>();
    for (const c of conversations) {
      const o = dmOtherId(c, currentUserId);
      if (o) convByPartner.set(o.trim().toLowerCase(), c);
    }
    const vergeben = new Set<string>();
    const zeilen: DmZeile[] = [];
    for (const u of users) {
      if (u.id === currentUserId) continue;
      const conv =
        [u.id, u.email ?? "", u.username ?? ""]
          .map((k) => convByPartner.get(k.trim().toLowerCase()))
          .find((c) => !!c) ?? null;
      if (conv) vergeben.add(conv.id);
      zeilen.push({ key: u.id, userId: u.id, name: nameOf(u.id), conv });
    }
    // DMs mit Personen, die es in der Nutzerliste nicht (mehr) gibt.
    for (const c of conversations) {
      if (c.type !== "dm" || vergeben.has(c.id)) continue;
      const o = dmOtherId(c, currentUserId);
      zeilen.push({ key: c.id, userId: o, name: o ? nameOf(o) : "Direktnachricht", conv: c });
    }
    return zeilen.sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, users, currentUserId]);

  const dmIds = useMemo(
    () => dmZeilen.map((z) => z.userId).filter((x): x is string => !!x),
    [dmZeilen],
  );
  const presence = usePresence(dmIds);


  const unreadOf = (id: string | null, fallback = 0): number =>
    (id ? chat.byConversation[id]?.unreadCount : undefined) ?? fallback;

  // Listen-Filter „Personen oder Teams suchen".
  const q = query.trim().toLowerCase();
  const filteredDms = useMemo(
    () => (!q ? dmZeilen : dmZeilen.filter((z) => z.name.toLowerCase().includes(q))),
    [dmZeilen, q],
  );
  const filteredTeams = useMemo(
    () => (!q ? teams : teams.filter((t) => t.name.toLowerCase().includes(q))),
    [teams, q],
  );
  // ---- Zeilen-Renderer ----

  // Eine Person: mit bestehender DM inkl. Vorschau/Zeit/Ungelesen, sonst nur der
  // Name — der Klick legt die DM dann erst an.
  const dmRow = (z: DmZeile) => {
    const c = z.conv;
    const unread = c ? unreadOf(c.id, c.unreadCount ?? 0) : 0;
    const isActive = !!c && c.id === activeId;
    const vorschau = c ? previewText(c.lastMessage, false) : "";
    return (
      <button
        key={z.key}
        type="button"
        onClick={() => (c ? onSelect(c.id) : z.userId && onOpenDmUser(z.userId))}
        style={rowStyle(isActive)}
      >
        <InitialsAvatar
          name={z.name}
          size={44}
          userId={z.userId}
          presence={z.userId ? <PresenceDot status={presence[z.userId]} size={10} /> : undefined}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={topLine}>
            <span style={titleStyle(unread > 0 || isActive)}>{z.name}</span>
            {c?.lastMessageAt && <span style={timeStyle}>{fmtListTime(c.lastMessageAt)}</span>}
          </div>
          {vorschau && <div style={subStyle(unread > 0)}>{vorschau}</div>}
        </div>
        {unread > 0 && <span style={badge}>{unread > 99 ? "99+" : unread}</span>}
      </button>
    );
  };

  const teamRow = (t: ChatTeamSummary) => {
    const unread = unreadOf(t.conversationId, t.unreadCount);
    const isActive = !!t.conversationId && t.conversationId === activeId;
    const name = gruppenName(t.name);
    return (
      <button key={t.teamId} type="button" onClick={() => onOpenTeam(t.teamId)} style={rowStyle(isActive)}>
        <InitialsAvatar name={t.name} size={44} square label={teamKuerzel(name)} bg={avatarColor(t.name)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={topLine}>
            <span style={titleStyle(unread > 0 || isActive)}>{name}</span>
            {t.lastMessageAt && <span style={timeStyle}>{fmtListTime(t.lastMessageAt)}</span>}
          </div>
          <div style={subStyle(unread > 0)}>
            {previewText(t.lastMessage, true) || `${t.memberCount} Mitglieder`}
          </div>
        </div>
        {unread > 0 && <span style={badge}>{unread > 99 ? "99+" : unread}</span>}
      </button>
    );
  };

  const sectionLabel = (label: string) => (
    <div style={{ padding: "12px 14px 4px", fontSize: 11, fontWeight: 800, letterSpacing: 0.7, color: INK2, textTransform: "uppercase" }}>
      {label}
    </div>
  );
  const emptyHint = (text: string) => (
    <div style={{ padding: 18, color: INK2, fontSize: 13, fontStyle: "italic" }}>{text}</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Suche + Neu */}
      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, padding: "12px 12px", flexShrink: 0 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: INK2, display: "inline-flex" }}>
            <Search20Regular />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Personen oder Teams suchen…"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "9px 12px 9px 36px",
              border: `1px solid ${LINE}`,
              borderRadius: 10,
              fontSize: 13.5,
              fontFamily: "inherit",
              background: "#fff",
              color: INK,
              outline: "none",
            }}
          />
        </div>
        <button
          type="button"
          onClick={onNewChat}
          aria-label="Neue Nachricht an mehrere"
          title="Neue Nachricht an mehrere"
          style={{
            flexShrink: 0,
            width: 38,
            height: 38,
            borderRadius: 10,
            border: `1px solid ${LINE}`,
            background: newChatOpen ? CREAM : "#fff",
            color: AUB,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Add20Regular />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, padding: "2px 12px 10px", flexShrink: 0 }}>
        {(["alle", "direkt", "teams"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              border: "none",
              background: tab === t ? PLUM : "transparent",
              color: tab === t ? "#fff" : INK2,
              fontWeight: 700,
              padding: "6px 16px",
              borderRadius: 999,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {/* Inhalt */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", borderTop: `1px solid ${LINE}` }}>
        {tab === "direkt" &&
          (filteredDms.length === 0 ? emptyHint("Keine Person gefunden.") : filteredDms.map(dmRow))}

        {tab === "teams" &&
          (filteredTeams.length === 0
            ? emptyHint("Keine Gruppen vorhanden.")
            : filteredTeams.map(teamRow))}

        {tab === "alle" && (
          <>
            {sectionLabel("Direktnachrichten")}
            {filteredDms.length === 0 ? emptyHint("Keine Person gefunden.") : filteredDms.map(dmRow)}
            {filteredTeams.length > 0 && (
              <>
                {sectionLabel("Teams")}
                {filteredTeams.map(teamRow)}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const rowStyle = (active: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 11,
  width: "100%",
  textAlign: "left",
  border: "none",
  borderLeft: active ? `3px solid ${AUB}` : "3px solid transparent",
  background: active ? ACTIVE_BG : "transparent",
  // Links 9px + 3px Border = 12px, bewusst kompensiert, damit aktive UND inaktive
  // Zeilen identisch bei 12px starten und mit Suchfeld/Tabs fluchten.
  padding: "9px 12px 9px 9px",
  cursor: "pointer",
});

const topLine: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
};

const titleStyle = (bold: boolean): React.CSSProperties => ({
  fontWeight: bold ? 800 : 600,
  color: INK,
  fontSize: 14,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const subStyle = (strong: boolean): React.CSSProperties => ({
  color: strong ? INK : INK2,
  fontSize: 12.5,
  fontWeight: strong ? 600 : 400,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginTop: 1,
});

const timeStyle: React.CSSProperties = { color: INK2, fontSize: 11, flexShrink: 0 };

const badge: React.CSSProperties = {
  flexShrink: 0,
  minWidth: 20,
  height: 20,
  padding: "0 6px",
  borderRadius: 999,
  background: GOLD,
  color: "#3a1d1a",
  fontSize: 11.5,
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

export default ConversationList;
